import Div from '@/shared/Div';

import { APIProvider, Map } from '@vis.gl/react-google-maps';
import React, { useCallback, useEffect, useState } from 'react';
import locator from '../../../public/locator.svg';
import ImageWithFallback from '@/globalComponents/image';
import FixedWrapper from '@/shared/FixedWrapper';
import Button from '@/shared/Button';
import Paragraph from '@/shared/Paragraph';
import Locator from '@/components/Icons/Locator';
import debounce from '@/utils/debounce';
import useModalToggle from '@/hooks/useModalToggle';
import Modal from '@/shared/crisp/Modal';
import ServiceAddressForm from './ServiceAddressForm';
import SearchBar from '@/shared/crisp/SearchBar';
import useGeolocation from '@/hooks/useGeoLocation';
import {
  fetchLocationByPlaceId,
  fetchLocationByReverseGeoCode,
  fetchLocationFromLatLong,
  fetchLocationSuggestions,
} from '@/actions/fetchMapLocation';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import SuggestedListMap from '../Map';
import { fetchServiceAblePincode } from '@/actions/serviceablePincode';
import useToast from '@/hooks/useToast';
import LocationTarget from '../Icons/LocationTarget';
import {
  FETCHING_CURRENT_LOCATION,
  LOCATION_PERMISSION_ERRORS,
  USER_LOCATION_PERMISSION_DENIED,
} from '@/constants/amc';
import AppBar from '@/shared/AppBar';

import useScreen from '@/hooks/useScreen';
import { useServiceContext } from '@/context/serviceRequestContext';
import ServiceLocationToolTip from './ServiceLocationToolTip';
import classNames from 'classnames';
import SmallLocator from '../Icons/SmallLocator';
import Divider from '@/shared/Divider';
import { getFirstWord } from '@/utils/string';
import ContentLoader from 'react-content-loader';
import useOnMount from '@/hooks/useOnMount';
import LocationPermission from './LocationPermission';
import { useProductContext } from '@/context/productContext';
import { getAddress } from '@/utils/address';
import dlv from 'dlv';
import { getEnvValue } from '@/utils/envUtils';

const Loader = (props: any) => (
  <ContentLoader
    speed={2}
    width={250}
    height={72}
    viewBox="0 0 250 72"
    backgroundColor="#f3f3f3"
    foregroundColor="#ecebeb"
    {...props}
  >
    <circle cx="583" cy="531" r="15" />
    <rect x="483" y="498" rx="2" ry="2" width="140" height="10" />
    <rect x="488" y="526" rx="2" ry="2" width="140" height="10" />
    <rect x="859" y="794" rx="2" ry="2" width="400" height="400" />
    <circle cx="609" cy="557" r="66" />
    <rect x="464" y="483" rx="0" ry="0" width="308" height="73" />
    <rect x="571" y="502" rx="0" ry="0" width="52" height="74" />
    <rect x="543" y="495" rx="0" ry="0" width="82" height="107" />
    <rect x="102" y="122" rx="0" ry="0" width="176" height="39" />
    <rect x="501" y="118" rx="0" ry="0" width="324" height="49" />
    <rect x="198" y="53" rx="0" ry="0" width="24" height="2" />
    <rect x="242" y="46" rx="0" ry="0" width="1" height="0" />
    <rect x="242" y="43" rx="0" ry="0" width="89" height="3" />
    <rect x="-22" y="19" rx="0" ry="0" width="341" height="83" />
  </ContentLoader>
);
const API_KEY = getEnvValue('GOOGLE_MAP_API_KEY');

function MapLocator() {
  const {
    location: currentLocation,
    error,
    isLoading,
    getLocation,
    permissionState,
  } = useGeolocation();
  const { dispatch: serviceAction } = useServiceContext();
  const { state: productState } = useProductContext();
  const [searchBoxFocus, setSearchBoxFocus] = useState(true);
  const [showToolTip, setshowToolTip] = useState(true);
  const [latLong, setLatLong] = useState<null | { lat: number; lng: number }>(null);
  const [callGeoAPI, setCallGeoAPI] = useState(true);
  const [location, setLocation] = useState({ preciseLocation: '', generalLocation: '' });
  const [suggestionList, setSuggestionList] = useState([]);
  const [pincode, setPincode] = useState('');
  const [addressDetails, setaddressDetails] = useState({
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    landmark: '',
    city: '',
    state: '',
  });
  const [disableCurrentLocationButton, setDisableCurrentLocationButton] = useState(false);
  const { isDesktop } = useScreen();

  const fetch = (...args: any) => fetchLocationFromLatLong.apply(null, args);
  const locationByLatLong = useTriggerRequest({ fetch });
  const fetchAddressByPlaceId = (...args: any) => fetchLocationByPlaceId.apply(null, args);
  const locationsData = useTriggerRequest({ fetch: fetchAddressByPlaceId });
  const fetchAddressSuggestions = (...args: any) => fetchLocationSuggestions.apply(null, args);
  const locationSuggestions = useTriggerRequest({ fetch: fetchAddressSuggestions });
  const fetchPincodeAvailablity = (...args: any) => fetchServiceAblePincode.apply(null, args);
  const serviceablePincode = useTriggerRequest({ fetch: fetchPincodeAvailablity });

  const fetchLocationByAddress = (...args: any) => fetchLocationByReverseGeoCode.apply(null, args);
  const reverseGeoCodeData = useTriggerRequest({ fetch: fetchLocationByAddress });
  const { open, openModal, closeModal } = useModalToggle(false);
  const {
    open: openLocationPermission,
    openModal: openLocationPermissionModal,
    closeModal: closeLocationPermissionModal,
  } = useModalToggle(false);

  const { triggerDefault } = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInputChange = useCallback(
    debounce(async (value) => {
      let suggestedLocations;
      if (value !== '') {
        suggestedLocations = await locationSuggestions.promisifyTrigger(value);
      } else {
        setSuggestionList([]);
      }
      setSuggestionList(suggestedLocations?.data);
    }, 500),
    [suggestionList],
  );

  const handleCurrentLocationClick = () => {
    getLocation();
    setSearchBoxFocus(false);
    if (error && LOCATION_PERMISSION_ERRORS.PERMISSION_DENIED) {
      triggerDefault({
        message: USER_LOCATION_PERMISSION_DENIED,
        durationInms: 4000,
      });
    } else {
      triggerDefault({
        message: FETCHING_CURRENT_LOCATION,
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLocationChange = useCallback(
    debounce(async (lat: number, lng: number) => {
      setPincode('');
      setLatLong({ lat, lng });
      const data = await locationByLatLong.promisifyTrigger(lat, lng);
      const { address, complete_address } = data?.data;
      const { flatNo, street, area, subArea, postal_code, landmark } = address || {};
      setaddressDetails((prevState) => ({
        ...prevState,
        address1: flatNo,
        address2: street,
        address3: subArea,
        address4: area,
        landmark,
      }));
      const locationData = {
        preciseLocation: `${getFirstWord(complete_address)}` || '',
        generalLocation: complete_address ?? '',
      };
      setPincode(postal_code);
      setLocation(locationData);
    }, 500),
    [],
  );
  useEffect(() => {
    if (permissionState !== 'granted') {
      setDisableCurrentLocationButton(true);
    } else {
      setDisableCurrentLocationButton(false);
    }
  }, [permissionState, disableCurrentLocationButton]);

  const handleLocationChanges = (event: any) => {
    const { lat, lng } = event.detail.center;
    setshowToolTip(false);
    if (callGeoAPI) {
      setLocation({ preciseLocation: '', generalLocation: '' });

      setLatLong({ lat, lng });
      handleLocationChange(lat, lng);
    } else {
      setCallGeoAPI(true);
    }
  };
  const handleConfirmClick = async () => {
    const data = await serviceablePincode.promisifyTrigger(pincode);

    if (data?.data) {
      const { city, state } = data?.data;
      setaddressDetails((prevState) => ({
        ...prevState,
        city,
        state,
      }));
      openModal();
    } else {
      triggerDefault({
        message: data?.error,
        durationInms: 5000,
      });
    }
  };

  useEffect(() => {
    const fetchAddress = async () => {
      const completeAddress = dlv(productState, 'selectedProduct.address', '');
      const data = await reverseGeoCodeData.promisifyTrigger(getAddress(completeAddress || ''));
      const { location } = data?.data;
      setLatLong(location);
    };
    if (error && error === LOCATION_PERMISSION_ERRORS.PERMISSION_DENIED) {
      openLocationPermissionModal();
      triggerDefault({
        message: USER_LOCATION_PERMISSION_DENIED,
        durationInms: 4000,
      });
      fetchAddress();
    }
  }, [error]);

  useEffect(() => {
    if (!isLoading && currentLocation && !error) {
      setLatLong(currentLocation);
      triggerDefault({
        message: FETCHING_CURRENT_LOCATION,
        durationInms: 3000,
      });
    }
  }, [currentLocation, isLoading, error]);

  const backButtonHandler = () => {
    serviceAction.changeComponent('bookASlot');
  };

  const handleLocationPermissionContinue = () => {
    closeLocationPermissionModal();
  };

  const handleLocationPermissionContinues = () => {
    getLocation();
    closeLocationPermissionModal();
    if (error && error === LOCATION_PERMISSION_ERRORS.PERMISSION_DENIED) {
      triggerDefault({
        message: USER_LOCATION_PERMISSION_DENIED,
        durationInms: 4000,
      });
    }
  };

  const handleSuggestionClick = async ({ place_id }: any) => {
    setCallGeoAPI(false);
    setLocation({ preciseLocation: '', generalLocation: '' });
    const data = (await locationsData.promisifyTrigger(place_id)) || {};
    setPincode('');
    const { address, location: latLongLocation, complete_address } = data?.data;
    const { flatNo, street, area, subArea, postal_code, landmark } = address || {};
    setaddressDetails((prevState) => ({
      ...prevState,
      address1: flatNo,
      address2: street,
      address3: subArea,
      address4: area,
      landmark,
    }));

    const locationData = {
      preciseLocation: `${getFirstWord(complete_address)}` || '',
      generalLocation: complete_address ?? '',
    };

    setLatLong(latLongLocation);

    if (postal_code) {
      setPincode(postal_code);
    } else {
      const profileInfo = await locationByLatLong.promisifyTrigger(
        latLongLocation.lat,
        latLongLocation.lng,
      );

      setPincode(profileInfo?.data?.address?.postal_code);
    }

    setLocation(locationData);
    setSuggestionList([]);
  };

  const renderCurrentLocationButton = () => {
    return (
      <Button
        onClick={handleCurrentLocationClick}
        theme="none"
        className={classNames(
          'mx-auto !rounded-cxs  border !bg-white border-blue-400 px-s5 py-s3 flex gap-s3 mb-4',
          {
            '!border-gray-300 !bg-gray-200': disableCurrentLocationButton,
          },
        )}
        disabled={disableCurrentLocationButton}
      >
        <LocationTarget fillColor={disableCurrentLocationButton ? '#6B7280' : undefined} />
        <Paragraph
          className={classNames('text-button-small text-blue-400', {
            ' !text-gray-400': disableCurrentLocationButton,
          })}
        >
          Use current location
        </Paragraph>
      </Button>
    );
  };

  const renderSuggestionList = useCallback((item: any, index: number) => {
    return (
      <Button
        theme="none"
        onClick={() => handleSuggestionClick(item)}
        className="flex gap-s3 !items-start  !w-full"
        key={index}
      >
        <Div className="flex-shrink-0 pt-s2">
          <SmallLocator />
        </Div>
        <Div className="flex flex-col gap-s2 items-start flex-grow text-left ">
          <Paragraph className="text-callout-normal">{getFirstWord(item.description)}</Paragraph>
          <Paragraph className="text-cti-secondary text-left">{item.description}</Paragraph>
        </Div>
      </Button>
    );
  }, []);

  const handleIdleState = () => {
    setshowToolTip(true);
  };

  const handleFocus = () => {
    setSearchBoxFocus(true);
  };
  const handleInputBlur = () => {
    setTimeout(() => {
      setSearchBoxFocus(false);
    }, 100);
  };
  return (
    <Div className="fixed inset-0 w-full max-w-screen-sm mx-auto mt-14 lg:mt-16">
      <AppBar
        fixed
        onClick={backButtonHandler}
        label="Service Address"
        onScrollLabel="Tell us the issue"
        preventScrollBackground={isDesktop}
      />

      <Div className=" z-50 ">
        <SearchBar
          onFocus={handleFocus}
          className="absolute top-[80px] md:top-[120px] left-s5 right-s5  z-[10] bg-black "
          onInputChange={handleInputChange}
          onBlur={handleInputBlur}
        />
        <Div
          className={classNames(
            'px-s6 pb-s6 flex flex-col gap-s6 absolute top-[150px] md:top-[180px] left-s5 right-s5  ',
            {
              invisible: !searchBoxFocus,
            },
          )}
        >
          {suggestionList?.length ? (
            <>
              <Paragraph className="text-body-xs text-cti-tertiary">SEARCH RESULTS</Paragraph>
            </>
          ) : null}
          <SuggestedListMap
            className="flex flex-col gap-s5 overflow-y-scroll "
            style={{ maxHeight: 'calc(100vh - 400px)' }}
            list={suggestionList}
            render={renderSuggestionList}
          />
        </Div>
      </Div>

      <Div
        className={classNames('relative w-full', {
          invisible: searchBoxFocus,
        })}
      >
        <APIProvider apiKey={API_KEY}>
          <Div className="w-full ">
            {latLong ? (
              <Map
                defaultZoom={18}
                defaultCenter={latLong}
                center={latLong}
                onCameraChanged={handleLocationChanges}
                streetViewControl={false}
                zoomControl={false}
                fullscreenControl={false}
                mapTypeControl={false}
                style={{
                  position: 'relative',
                  height: 'calc(100vh - 121px)',
                }}
                onIdle={handleIdleState}
              >
                <Div
                  className="absolute z-50 flex flex-col items-center gap-s5"
                  style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                >
                  <ServiceLocationToolTip
                    className={classNames('z-50 transform -translate-y-1/2 h-auto  w-[230px] ', {
                      invisible: !showToolTip,
                    })}
                    dataTestId="service-location-toolTip"
                  />

                  <ImageWithFallback
                    src={locator}
                    alt="map pin"
                    className="transform -translate-y-1/2"
                    data-testid="downArrowIcon"
                  />
                </Div>
              </Map>
            ) : null}
          </Div>
        </APIProvider>

        <FixedWrapper>
          {renderCurrentLocationButton()}
          <Div
            className={classNames(
              'p-6 pt-4 md:pb-4 bg-white shadow-subtle flex flex-col gap-s3 items-center',
              {
                invisible: searchBoxFocus,
              },
            )}
          >
            {location.preciseLocation !== '' || location.generalLocation !== '' ? (
              <Div className="flex flex-col gap-s3 w-full max-w-screen-sm mx-auto">
                <Div className="flex gap-s3">
                  <Locator />
                  <Paragraph className="text-h-3">{location.preciseLocation || ''}</Paragraph>
                </Div>
                <Paragraph>{location.generalLocation || ''}</Paragraph>
              </Div>
            ) : (
              <Loader />
            )}

            <Div className="py-s5 w-full max-w-screen-sm mx-auto">
              <Button className="!w-full" onClick={handleConfirmClick}>
                Select this location
              </Button>
            </Div>
          </Div>
        </FixedWrapper>
      </Div>

      <Modal open={open} onClose={closeModal}>
        <ServiceAddressForm
          closeModal={closeModal}
          location={location}
          pincode={pincode}
          addressDetails={addressDetails}
          latLong={latLong}
        />
      </Modal>
      <Modal open={openLocationPermission} onClose={closeLocationPermissionModal}>
        <LocationPermission onContinue={handleLocationPermissionContinue} />
      </Modal>
    </Div>
  );
}

export default MapLocator;
